import {useEffect, useRef, useState} from "react";
import {CircularProgress} from "@mui/material";
import type {SxProps, Theme} from "@mui/material";
import {Colors} from "@bounce/shared/ui";
import {Button} from "./Button";

export type SyncPhase = "idle" | "syncing" | "synced";

const SYNCED_RESET_MS = 2500;

const SYNC_ANIMATIONS = {
  "@keyframes syncFlash": {
    "0%": {
      backgroundColor: "transparent",
      borderColor: Colors.green._400,
      color: Colors.green._700,
      boxShadow: "none",
    },
    "18%": {
      backgroundColor: Colors.green._600,
      borderColor: Colors.green._600,
      color: "#fff",
      boxShadow: `0 0 8px 2px ${Colors.green._200}`,
    },
    "55%": {
      backgroundColor: Colors.green._600,
      borderColor: Colors.green._600,
      color: "#fff",
      boxShadow: `0 0 0 0 transparent`,
    },
    "78%": {
      backgroundColor: Colors.green._100,
      borderColor: Colors.green._600,
      color: Colors.green._800,
    },
    "100%": {
      backgroundColor: "transparent",
      borderColor: Colors.green._600,
      color: Colors.green._800,
      boxShadow: "none",
    },
  },
};

const CHECK_PATH_LENGTH = 24;

const DrawCheck = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{overflow: "visible"}}>
    <path
      d="M4 11 L8.5 15.5 L16.5 5.5"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        strokeDasharray: CHECK_PATH_LENGTH,
        strokeDashoffset: CHECK_PATH_LENGTH,
        animation: "checkDraw 0.45s cubic-bezier(0.65, 0, 0.35, 1) 0.35s forwards",
      }}
    />
  </svg>
);

const baseSx = {flexShrink: 0, whiteSpace: "nowrap", height: 36, minWidth: 80, gap: 0.75} as const;

const syncedSx = {
  ...baseSx,
  ...SYNC_ANIMATIONS,
  "@keyframes checkDraw": {
    "0%": {strokeDashoffset: CHECK_PATH_LENGTH},
    "100%": {strokeDashoffset: 0},
  },
  animation: "syncFlash 2.2s ease-in-out forwards",
  borderColor: Colors.green._600,
  color: Colors.green._800,
  "&:hover": {borderColor: Colors.green._700, backgroundColor: Colors.green._100},
};

const PHASE_LABELS: Record<SyncPhase, string> = {idle: "Sync", syncing: "Syncing", synced: "Synced"};

/** Drives a SyncButton through syncing -> synced -> idle. Returns undefined when the sync failed. */
export const useSyncPhase = () => {
  const [phase, setPhase] = useState<SyncPhase>("idle");
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const runSync = async <T,>(sync: () => Promise<T>): Promise<T | undefined> => {
    setPhase("syncing");
    try {
      const result = await sync();
      setPhase("synced");
      resetTimer.current = setTimeout(() => setPhase("idle"), SYNCED_RESET_MS);
      return result;
    } catch {
      setPhase("idle");
      return undefined;
    }
  };

  return {phase, runSync};
};

type Props = {
  phase: SyncPhase;
  onClick: () => void;
  label?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
};

export const SyncButton = ({phase, onClick, label, disabled, sx}: Props) => (
  <Button
    type="button"
    size="small"
    buttonType="outlined-primary"
    onClick={onClick}
    disabled={disabled || phase === "syncing"}
    sx={{...(phase === "synced" ? syncedSx : baseSx), ...sx}}
  >
    {phase === "syncing" && <CircularProgress size={14} sx={{color: "inherit"}} />}
    {phase === "synced" && <DrawCheck />}
    {phase === "idle" ? label ?? PHASE_LABELS.idle : PHASE_LABELS[phase]}
  </Button>
);
